import { useCallback, useState } from 'react';
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { Link } from 'react-router-dom';
import { ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import ClientForm from "../components/ClientForm";
import ClientsFilters from './ClientsFilters';
import {ClientService} from "../../../services/clients/clientService";
import {ClientsApiResponse} from "../../../type/entities/client-type";

const columnOrder = [
    'company',
    'id',
    'companyName',
    'cifNif',
    'address',
    'email',
    'telephone',
    'createdAt',
    'updatedAt',
];

interface ClientsListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    clientSelected: any;
    setClientSelected: React.Dispatch<React.SetStateAction<any>>;
}

const ClientsList: React.FC<ClientsListProps> = ({ openFormDrawer, setOpenFormDrawer, clientSelected, setClientSelected }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new ClientService();


    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene el listado de clientes
     *
     * @EN Gets the list of clients
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listClients(filters);
        return response.getResponseData() as ClientsApiResponse[];
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de un cliente existente
     *
     * @EN Handles the deletion of an existing client
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteClient(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Cliente eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de múltiples clientes
     *
     * @EN Handles the deletion of multiple clients
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiClients(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Clientes eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'clients-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                         {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'companyName',
                            render: (row: any) => {
                                let name = row.row.original.companyName;
                                return (
                                    <a href={`/clients/${row.row.original.id}/profile`} className='text-primary hover:text-primary hover:underline'>
                                        {name}
                                    </a>
                                );
                            }

                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'client', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.clients.path}/${row.id}/profile/${menuRoutes.clients.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'client', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setClientSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'client', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar cliente`}
                                                description={`¿Estás seguro de que deseas eliminar el cliente ${row.companyName}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ClientsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Clientes'} onClear={() => resetFilters(50)}>
                <ClientsFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    clientSelected ?
                        "Completa el formulario para editar los datos del cliente" :
                        "Completa el formulario para crear un nuevo cliente en el sistema"
                }
                drawerTitle={clientSelected ? 'Editar Cliente' : 'Crear Cliente'} submitText={clientSelected ? 'Guardar Cliente' : 'Crear Cliente'}>
                <ClientForm setOpenModal={setOpenFormDrawer} clientSelected={clientSelected} refetch={refetch} />
            </FormDrawerView>
        </Page>
    );
};

export default ClientsList;